import { setDay, format } from 'date-fns/esm'
import { dateUtils } from '@/utils'
import { useDateFnsOptions } from '@/hooks'
import { useCallback, useState } from 'react'
import { useIntl } from '@/intl'
import { RecurrenceWhen } from '../../../../types'

export function useCreateFormatRecurrenceWhenLabel() {
  const { weekStartsOn, locale } = useDateFnsOptions()
  const texts = useTexts()

  return useCallback(
    (recurrenceWhen: RecurrenceWhen) => {
      switch (recurrenceWhen.type) {
        case 'WEEKLY': {
          const { weekday } = recurrenceWhen
          const date = setDay(new Date(), weekday, { weekStartsOn })
          return format(date, 'EEEE', { locale })
        }

        case 'MONTHLY': {
          const { date, cardinal } = recurrenceWhen
          const parsedDate = dateUtils.parseDate(date)

          if (typeof cardinal === 'number') {
            const cardinalDate = new Date()
            cardinalDate.setDate(cardinal)

            const cardinalText = format(cardinalDate, 'do', { locale })
            const dayText = format(parsedDate, 'EEEE', { locale })
            return texts.monthly.cardinal(cardinalText, dayText)
          }

          const dateText = format(parsedDate, 'do', { locale })
          return texts.monthly.date(dateText)
        }

        case 'ANNUALLY': {
          const { date, cardinal } = recurrenceWhen
          const parsedDate = dateUtils.parseDate(date)

          if (typeof cardinal === 'number') {
            const cardinalDate = new Date()
            cardinalDate.setDate(cardinal)

            const cardinalText = format(cardinalDate, 'do', { locale })
            const dayText = format(parsedDate, 'EEEE', { locale })
            const monthText = format(parsedDate, 'MMMM', { locale })

            return texts.annually.cardinal(cardinalText, dayText, monthText)
          }

          const dateText = format(parsedDate, 'MMM d', { locale })
          return texts.annually.date(dateText)
        }
      }
    },
    [weekStartsOn, locale, texts],
  )
}

function useTexts() {
  const { translate } = useIntl()

  const [api] = useState(() => ({
    monthly: {
      cardinal: (cardinal: string, date: string) =>
        translate({
          id: 'recurrenceSettings.recurrenceWhen.monthly.cardinal',
          values: { cardinal, date },
        }),
      date: (date: string) =>
        translate({ id: 'recurrenceSettings.recurrenceWhen.monthly', values: { date } }),
    },
    annually: {
      cardinal: (cardinal: string, day: string, month: string) =>
        translate({
          id: 'recurrenceSettings.recurrenceWhen.annually.cardinal',
          values: { cardinal, day, month },
        }),
      date: (date: string) =>
        translate({ id: 'recurrenceSettings.recurrenceWhen.annually', values: { date } }),
    },
  }))

  return api
}
